import WDBox from "../../../../../MaterialComponents/WDBox";
import WDTypography from "../../../../../MaterialComponents/WDTypography";
import { useEffect, useState } from "react";
import AppService from "../../../../../Configurations/Appservice";
import apiService from "../../../../../services/api";
import { Link, CircularProgress, Icon, Box } from "@mui/material";
import WDButton from "../../../../../MaterialComponents/WDButton";
import pxToRem from "../../../../../Themes/Functions/pxToRem";
import DocumentField from "../../../../../Components/DocumentField";
import ViewDocument from "../../../../../Components/ViewDocument";
import { useDispatch } from "react-redux";
import { showLoader, dismissLoader } from "../../../../../LayoutContainers/AppLayout/redux/AppSlice";

const FileField = ({ field, onFileUpload, index, onFileRemove, employeeUid }) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState();
  const [isFileUploading, fileUploading] = useState(false);

  // Map field name to backend document type
  const toBackendDocumentType = (fieldName) => {
    if (!fieldName) return null;
    switch (fieldName) {
      case 'passportCopy': return 'passport_copy';
      case 'visaPage': return 'visa_page';
      case 'salaryCertificate': return 'salary_certificate';
      case 'birthCertificate': return 'birth_certificate';
      case 'emiratesId': return 'emirates_id';
      case 'photo': return 'photo';
      case 'shinasnama': return 'shinasnama';
      case 'labourContract': return 'labour_contract';
      default:
        return fieldName.replace(/([A-Z])/g, '_$1').toLowerCase();
    }
  };

  // List of static document fields
  const staticDocumentFields = [
    'birthCertificate', 'passportCopy', 'visaPage', 'salaryCertificate',
    'photo', 'shinasnama', 'labourContract', 'emiratesId',
    'resume', 'educationalCertificate', 'experienceCertificate', 'policeClearanceCertificate'
  ];

  const onFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const _onFileRemove = () => {
    setFile();
    onFileRemove(field, index);
  };

  useEffect(() => {
    if (file) {
      fileUpload();
    }
  }, [file]);

  // Uses simplified 2-API flow: /v1/files/upload then /v1/employee-documents/{id}/bulk
  const fileUpload = async () => {
    fileUploading(true);
    dispatch(showLoader("Uploading document..."));
    
    try {
      const currentEmployeeUid = employeeUid || window.currentEmployeeUid;
      const backendDocumentType = toBackendDocumentType(field.name);
      const isStaticDocField = staticDocumentFields.includes(field.name);
      
      console.log('FileField upload starting:', {
        fieldName: field.name,
        employeeUid: currentEmployeeUid,
        documentType: backendDocumentType,
        isStaticDocField
      });

      // Step 1: Upload the file with documentType and employeeUid
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('documentType', backendDocumentType);
      uploadFormData.append('employeeUid', currentEmployeeUid);
      uploadFormData.append('fileName', file.name);

      console.log('Step 1: Uploading file to /v1/files/upload');
      
      const uploadResp = await apiService.api.post(
        '/v1/files/upload',
        uploadFormData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      const uploadResult = uploadResp.data || {};
      console.log('File upload successful:', uploadResult);

      // Extract filePath from upload response
      const filePath = uploadResult.filePath || 
                      uploadResult.data?.filePath || 
                      uploadResult.path ||
                      uploadResult.data?.path;

      console.log('Extracted filePath:', filePath);

      if (!filePath) {
        throw new Error('Failed to get filePath from upload response');
      }

      // Step 2: Call bulk API to save document (only for static document fields with employeeUid)
      let documentGroupId = null;
      let bulkDocumentId = null;

      if (currentEmployeeUid && backendDocumentType && isStaticDocField) {
        console.log('Step 2: Calling /v1/employee-documents/{id}/bulk');

        const bulkPayload = {
          documentType: backendDocumentType,
          replaceExisting: false,
          documents: [{
            filePath: filePath,
            fileName: file.name,
            fileSize: file.size
          }]
        };

        // Multiple-instance certificate types (educational/experience) are keyed by a
        // document name (title). There is no name prompt during ticket creation, so
        // derive one from the file name to satisfy the backend's title requirement.
        const MULTI_INSTANCE_DOC_TYPES = ['educational_certificate', 'experience_certificate'];
        if (MULTI_INSTANCE_DOC_TYPES.includes(backendDocumentType)) {
          const baseName = file.name.replace(/\.[^/.]+$/, '').trim();
          bulkPayload.title = baseName || file.name;
          bulkPayload.isReplace = false;
        }

        console.log('Bulk API payload:', bulkPayload);

        try {
          const bulkResp = await apiService.api.post(
            `/v1/employee-documents/${currentEmployeeUid}/bulk`,
            bulkPayload,
            { headers: { 'Content-Type': 'application/json' } }
          );

          const bulkResult = bulkResp.data?.data || bulkResp.data || [];
          console.log('Bulk API response:', bulkResult);

          // Extract documentGroupId and id from bulk response
          if (Array.isArray(bulkResult) && bulkResult.length > 0) {
            documentGroupId = bulkResult[0].documentGroupId;
            bulkDocumentId = bulkResult[0].id;
            console.log('Extracted from bulk response:', { documentGroupId, bulkDocumentId });
          }
        } catch (bulkError) {
          console.error('Bulk API error:', bulkError);
          // Continue even if bulk fails - file is already uploaded
        }
      }

      // Create file metadata with documentGroupId for documentReferences
      const fileMetadata = {
        id: bulkDocumentId || filePath,
        fileName: file.name,
        filePath: filePath,
        fileSize: file.size,
        fileType: file.type,
        documentType: backendDocumentType,
        documentGroupId: documentGroupId,
        uploadTime: new Date().toISOString()
      };

      console.log('Created fileMetadata object:', fileMetadata);

      // Call the parent's onFileUpload callback with the file metadata
      onFileUpload(fileMetadata, field, index);

    } catch (error) {
      console.error('Error during file upload:', error);
      alert(`Error uploading file: ${error.message}`);
      setFile();
    } finally {
      dispatch(dismissLoader());
      fileUploading(false);
    }
  };

  const _renderActionButton = () => {
    if (isFileUploading) {
      return <CircularProgress size={16} />;
    }
    if (!field.value) {
      return null;
    }
    return (
      <Icon fontSize="small" onClick={_onFileRemove}>
        delete
      </Icon>
    );
  };

  const _renderFileArea = () => {
    if (field.value) {
      return (
        // <WDButton
        //   variant="text"
        //   component={Link}
        //   style={{ fontSize: "10px" }}
        //   target="_blank"
        //   href={field.value}
        //   underline="hover"
        // >
        //   View uploaded {field.type}
        // </WDButton>
        <ViewDocument label={field.type} url={field.value} fileName="test2" />
      );
    }

    let acceptedType = "image/*";
    if (field.type == "PDF") {
      // Accept both PDF and images for document fields like passport, visa, etc.
      acceptedType = "application/pdf,image/*";
    } else if (field.type == "DOCUMENT") {
      acceptedType =
        ".doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/*";
    }

    // accept="application/pdf
    return (
      <WDButton variant="text" component="label" style={{ fontSize: "10px" }}>
        {file ? file.name : `Select ${field.type}`}
        <input type="file" hidden onChange={onFileChange} accept={acceptedType} />
      </WDButton>
    );
  };

  return (
    <Box>
      <WDTypography
        variant="caption"
        sx={{ color: field.mandatory && !field.value ? "red" : null }}
      >
        {field.title}&nbsp;{field.mandatory ? "*" : ""}
      </WDTypography>
      <WDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        p={1}
        sx={{
          border: 1,
          borderColor: "grey.400",
          borderRadius: pxToRem(6),
          height: pxToRem(45),
        }}
      >
        {_renderFileArea()}
        {_renderActionButton()}
      </WDBox>
    </Box>
  );
};

export default FileField;
